import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ArrowRight, Database, Zap, GitBranch, Shield, Target } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-indigo-800 to-indigo-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-purple-500/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <RefreshCw className="w-16 h-16 text-white/90" />
            </div>
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl md:text-6xl">
              Seamless EMR Integration Platform
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-indigo-100">
              Connect your 4D EMR system with leading CRMs for synchronized patient data and streamlined workflows.
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/api"
                className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-700/50"
              >
                API Documentation
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Enterprise-Grade Integration Platform
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              Powerful features designed to seamlessly connect your healthcare systems.
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-indigo-600 mb-4">
                <Database className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">
                Bi-Directional Sync
              </h3>
              <p className="text-gray-500">
                Real-time synchronization of patient data and appointments between your EMR and CRM systems.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-indigo-600 mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">
                HIPAA Compliant
              </h3>
              <p className="text-gray-500">
                Enterprise-grade security with end-to-end encryption and full HIPAA compliance for patient data.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-indigo-600 mb-4">
                <GitBranch className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">
                Advanced Mapping
              </h3>
              <p className="text-gray-500">
                Flexible field mapping and data transformation between your EMR and CRM systems.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">How It Works</h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              Get started in minutes with our simple integration process
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="relative">
              <div className="absolute top-0 left-1/2 -ml-1 h-full w-0.5 bg-gray-200 md:hidden" />
              <div className="relative flex flex-col items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white">
                  1
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">Connect EMR</h3>
                <p className="mt-2 text-center text-gray-500">
                  Connect your 4D EMR using your subscription key and credentials
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute top-0 left-1/2 -ml-1 h-full w-0.5 bg-gray-200 md:hidden" />
              <div className="relative flex flex-col items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white">
                  2
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">Configure CRM</h3>
                <p className="mt-2 text-center text-gray-500">
                  Set up your CRM connection and configure data mapping
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="relative flex flex-col items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white">
                  3
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">Start Syncing</h3>
                <p className="mt-2 text-center text-gray-500">
                  Watch your patient data sync automatically between systems
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-indigo-700">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="bg-indigo-600 rounded-lg shadow-xl overflow-hidden">
            <div className="pt-10 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:pr-0 xl:py-20 xl:px-20">
              <div className="lg:self-center">
                <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                  <span className="block">Ready to get started?</span>
                  <span className="block text-indigo-200">Connect your EMR today.</span>
                </h2>
                <p className="mt-4 text-lg leading-6 text-indigo-200">
                  Join healthcare providers already using our platform to streamline their patient data management.
                </p>
                <Link
                  to="/login"
                  className="mt-8 bg-white border border-transparent rounded-md shadow px-6 py-3 inline-flex items-center text-base font-medium text-indigo-600 hover:bg-indigo-50"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h4 className="text-white font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/api" className="hover:text-white">API Documentation</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            <div className="flex items-center justify-center space-x-4">
              <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
              <span className="text-gray-600">•</span>
              <Link to="/terms" className="text-gray-400 hover:text-white">Terms of Service</Link>
            </div>
            <p className="mt-4">© {new Date().getFullYear()} 4D EMR Integration Platform. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}